import { useStyleRegister } from '@ant-design/cssinjs';
import { ConfigProvider, Flex, Input, theme } from 'antd';
import PropTypes from 'prop-types';
import { useContext } from 'react';

const ComposerPanel = ({ actions, children, onSubmit, textAreaProps }) => {
  const { csp } = useContext(ConfigProvider.ConfigContext);
  const { hashId, theme: tokenTheme, token } = theme.useToken();
  const tokenKey = token._tokenKey || hashId || 'default';
  const themeClassName = `kip-composer-panel-${tokenKey}`;

  useStyleRegister(
    {
      hashId,
      layer: { name: 'antd' },
      nonce: () => csp?.nonce,
      path: ['ComposerPanel', tokenKey],
      theme: tokenTheme,
      token,
    },
    () => ({
      [`.${themeClassName}`]: {
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        '.kip-composer-content': {
          minHeight: 0,
          overflow: 'auto',
          padding: token.paddingLG,
        },
        '.kip-composer-form': {
          marginInline: token.marginLG,
          marginTop: token.marginSM,
          marginBottom: token.marginLG,
          '.kip-composer-actions > button': { height: 'auto' },
          '.kip-composer-input': {
            flex: 1,
            minWidth: 0,
            width: 'auto',
            // Reserve a count band outside the scrolling text, but inside its border.
            paddingBottom: token.fontSizeSM * token.lineHeightSM + token.paddingSM,
            '.kip-composer-textarea': { resize: 'none' },
            '.kip-composer-count': {
              bottom: token.paddingXS,
              insetInlineEnd: token.paddingSM,
              fontSize: token.fontSizeSM,
              lineHeight: token.lineHeightSM,
              color: token.colorTextDescription,
            },
            '&.kip-composer-input-error .kip-composer-count': { color: token.colorError },
          },
        },
      },
    }),
  );

  return (
    <Flex vertical flex={1} className={`kip-composer-panel ${themeClassName} ${hashId}`}>
      <Flex vertical flex={1} gap="large" className="kip-composer-content">
        {children}
      </Flex>
      <Flex
        component="form"
        align="stretch"
        flex="none"
        gap="small"
        className="kip-composer-form"
        onSubmit={onSubmit}
      >
        <Input.TextArea
          {...textAreaProps}
          classNames={{
            root: `kip-composer-input${textAreaProps.status === 'error' ? ' kip-composer-input-error' : ''}`,
            textarea: 'kip-composer-textarea',
            count: 'kip-composer-count',
          }}
        />
        <Flex align="stretch" flex="none" className="kip-composer-actions">
          {actions}
        </Flex>
      </Flex>
    </Flex>
  );
};

ComposerPanel.propTypes = {
  actions: PropTypes.node.isRequired,
  children: PropTypes.node,
  onSubmit: PropTypes.func.isRequired,
  textAreaProps: PropTypes.shape({ status: PropTypes.string }).isRequired,
};

export default ComposerPanel;
